import { Head, Link, router } from "@inertiajs/react"
import { useCallback, useMemo, useState } from "react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import type { SeoMeta } from "@/types"
import { getLocationBySlug, LOCATION_SLUGS, type Office } from "@/data/directory/registry"

/* ------------------------------------------------------------------ */
/* Office logo                                                          */
/* ------------------------------------------------------------------ */

function getInitials(name: string) {
  return name
    .split(/[\s&]+/)
    .filter((w) => w.length > 0 && w[0] === w[0].toUpperCase())
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
}

function getFaviconUrl(website: string, size = 64) {
  try {
    const { hostname } = new URL(website)
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=${size}`
  } catch {
    return null
  }
}

function OfficeLogo({
  office,
  size = "md",
}: {
  office: Office
  size?: "sm" | "md" | "lg"
}) {
  const [imgFailed, setImgFailed] = useState(false)
  const faviconUrl = getFaviconUrl(office.website)
  const initials = getInitials(office.name)

  const sizeClasses = {
    sm: "size-8 text-xs",
    md: "size-10 text-sm",
    lg: "size-14 text-lg",
  }[size]

  if (faviconUrl && !imgFailed) {
    return (
      <div className={`${sizeClasses} flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-stone bg-white`}>
        <OptimizedImage
          src={faviconUrl}
          alt={`${office.name} logo`}
          className="size-full object-contain p-1"
          onError={() => setImgFailed(true)}
        />
      </div>
    )
  }

  return (
    <div
      className={`${sizeClasses} flex shrink-0 items-center justify-center rounded-lg border border-stone bg-charcoal font-display font-bold text-cream`}
    >
      {initials}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Icons                                                                */
/* ------------------------------------------------------------------ */

function IconStar({ className = "size-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconMapPin({ className = "size-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="m9.69 18.933.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconCheck({ className = "size-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconExternalLink({ className = "size-3.5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Zm7.49-2.25a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0V4.81l-7.22 7.22a.75.75 0 1 1-1.06-1.06l7.22-7.22H11.74a.75.75 0 0 1-.75-.75Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Office card                                                          */
/* ------------------------------------------------------------------ */

function OfficeCard({
  office,
  location,
  selected,
  onToggle,
}: {
  office: Office
  location: string
  selected: boolean
  onToggle: (slug: string) => void
}) {
  return (
    <div
      className={`relative flex flex-col overflow-hidden rounded-xl border transition-all ${
        selected
          ? "border-red/30 ring-2 ring-red/20"
          : "border-stone hover:border-stone/80 hover:shadow-sm"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-0">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <OfficeLogo office={office} size="sm" />
          <div className="min-w-0">
            <Link
              href={`/directory/${location}/${office.slug}`}
              className="font-display text-lg font-bold text-charcoal hover:text-red transition-colors"
            >
              {office.name}
            </Link>
            <p className="text-sm text-slate">{office.branch}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onToggle(office.slug)}
          className={`flex size-7 shrink-0 items-center justify-center rounded-md border-2 transition-all ${
            selected
              ? "border-red bg-red text-white"
              : "border-stone bg-white text-transparent hover:border-stone/80"
          }`}
          aria-label={selected ? `Remove ${office.name} from comparison` : `Add ${office.name} to comparison`}
        >
          <IconCheck className="size-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 px-5 pt-3 pb-5">
        {/* Location */}
        <div className="flex items-center gap-1.5 text-sm text-slate">
          <IconMapPin className="size-3.5 shrink-0 text-warm-grey" />
          <span>{office.address}, {office.postcode}</span>
        </div>

        {/* Rating */}
        <div className="mt-2.5 flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <IconStar
                key={star}
                className={`size-3.5 ${
                  star <= Math.round(office.rating) ? "text-amber-400" : "text-stone"
                }`}
              />
            ))}
          </div>
          <span className="text-sm font-medium text-charcoal">{office.rating}</span>
          <span className="text-xs text-warm-grey">({office.reviewCount} reviews)</span>
        </div>

        {/* Specialisms */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {office.specialisms.slice(0, 3).map((s) => (
            <span
              key={s}
              className="rounded-full bg-stone/50 px-2.5 py-0.5 text-[11px] font-medium text-slate"
            >
              {s}
            </span>
          ))}
          {office.specialisms.length > 3 && (
            <span className="rounded-full bg-stone/50 px-2.5 py-0.5 text-[11px] font-medium text-warm-grey">
              +{office.specialisms.length - 3}
            </span>
          )}
        </div>

        {/* Key stats */}
        <div className="mt-4 flex items-center gap-4 text-xs text-warm-grey">
          <span>Est. {office.established}</span>
          <span className="h-3 w-px bg-stone" />
          <span>~{office.propertyCount} listings</span>
          <span className="h-3 w-px bg-stone" />
          <span>Avg. {office.avgPrice}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-3 border-t border-stone px-5 py-3">
        <Link
          href={`/directory/${location}/${office.slug}`}
          className="text-sm font-medium text-red transition-colors hover:text-deep-red"
        >
          View profile
        </Link>
        <a
          href={office.website}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-slate transition-colors hover:text-charcoal"
        >
          Website
          <IconExternalLink className="size-3" />
        </a>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Floating compare bar                                                 */
/* ------------------------------------------------------------------ */

function CompareBar({
  selected,
  location,
  onClear,
}: {
  selected: string[]
  location: string
  onClear: () => void
}) {
  const handleCompare = useCallback(() => {
    router.visit(`/directory/${location}/compare?offices=${selected.join(",")}`)
  }, [selected, location])

  if (selected.length === 0) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-stone bg-white/95 px-6 py-4 shadow-lg backdrop-blur-lg sm:bottom-4 sm:inset-x-auto sm:left-1/2 sm:w-auto sm:min-w-[28rem] sm:max-w-lg sm:-translate-x-1/2 sm:rounded-2xl sm:border sm:shadow-xl">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-charcoal">
            {selected.length} office{selected.length !== 1 && "s"} selected
          </p>
          <p className="truncate text-xs text-warm-grey">
            {selected.length >= 2 ? "Ready to compare" : "Select at least 2 to compare"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClear}
            className="rounded-lg px-3 py-2 text-sm font-medium text-slate transition-colors hover:bg-stone/50 hover:text-charcoal"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleCompare}
            disabled={selected.length < 2}
            className="rounded-lg bg-red px-5 py-2 text-sm font-semibold text-white transition-all hover:bg-deep-red disabled:cursor-not-allowed disabled:opacity-40"
          >
            Compare
          </button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* SEO copy per location                                                */
/* ------------------------------------------------------------------ */

const SEO_COPY: Record<string, { heading: string; paragraphs: string[] }> = {
  bristol: {
    heading: "Finding the right estate agent in Bristol",
    paragraphs: [
      "Bristol\u2019s property market is one of the most dynamic in the South West, with prices and demand varying significantly across postcodes. Choosing the right estate agent can make a real difference \u2014 whether you\u2019re selling a Georgian townhouse in Clifton, a family home in Henleaze, or a first-time buy in Southville.",
      "Our directory brings together the leading agents operating across Bristol, from national firms like Savills and Knight Frank to respected local independents such as Hollis Morgan, Alexander May, and Goodman & Lilley. Use the filters to narrow your search by area or specialism, or compare offices side by side to find the best fit.",
      "Every agent listed here is active in the Bristol market with a physical office. We include contact details, specialisms, areas covered, and links to their websites so you can get in touch directly.",
    ],
  },
  brighton: {
    heading: "Finding the right estate agent in Brighton",
    paragraphs: [
      "Brighton & Hove\u2019s property market is one of the most vibrant on the South Coast, with everything from Regency seafront apartments to village homes in Rottingdean and Saltdean. Choosing the right estate agent matters \u2014 the local market moves quickly and local knowledge counts.",
      "Our directory features leading agents across Brighton & Hove, from established names like Fox & Sons and Mansell McTaggart to well-regarded independents such as Brand Vaughan, Knight & Knoxley, and Foster & Co. Filter by area or specialism, or compare offices side by side.",
      "Every agent listed here has a physical office in the Brighton & Hove area. We include contact details, specialisms, areas covered, and links to their websites so you can get in touch directly.",
    ],
  },
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function DirectoryLocation({
  meta,
  location,
}: {
  meta: SeoMeta
  location: string
}) {
  const locationData = getLocationBySlug(location)

  const [selected, setSelected] = useState<string[]>([])
  const [filterSpecialism, setFilterSpecialism] = useState("")
  const [filterArea, setFilterArea] = useState("")
  const [search, setSearch] = useState("")

  const toggleOffice = useCallback((slug: string) => {
    setSelected((prev) =>
      prev.includes(slug)
        ? prev.filter((s) => s !== slug)
        : prev.length < 6
          ? [...prev, slug]
          : prev
    )
  }, [])

  const clearSelected = useCallback(() => setSelected([]), [])

  const offices = useMemo(() => locationData?.offices ?? [], [locationData?.offices])
  const specialisms = locationData?.specialisms ?? []
  const areas = locationData?.areas ?? []
  const cityName = locationData?.name ?? location

  const otherLocations = LOCATION_SLUGS.filter((s) => s !== location)

  const filtered = useMemo(() => {
    let result = offices

    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (o) =>
          o.name.toLowerCase().includes(q) ||
          o.branch.toLowerCase().includes(q) ||
          o.address.toLowerCase().includes(q) ||
          o.postcode.toLowerCase().includes(q)
      )
    }

    if (filterSpecialism) {
      result = result.filter((o) => o.specialisms.includes(filterSpecialism))
    }

    if (filterArea) {
      result = result.filter((o) => o.areas.includes(filterArea))
    }

    return result
  }, [offices, search, filterSpecialism, filterArea])

  const seoCopy = SEO_COPY[location]

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${cityName} Estate Agents Directory`,
            description: meta.description,
            numberOfItems: offices.length,
            itemListElement: offices.map((o, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "RealEstateAgent",
                name: `${o.name} \u2014 ${o.branch}`,
                address: {
                  "@type": "PostalAddress",
                  streetAddress: o.address,
                  addressLocality: cityName,
                  postalCode: o.postcode,
                  addressCountry: "GB",
                },
                telephone: o.phone,
                url: o.website,
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: o.rating,
                  reviewCount: o.reviewCount,
                },
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${new URL(meta.canonical).origin}/` },
              { "@type": "ListItem", position: 2, name: "Directory", item: `${new URL(meta.canonical).origin}/directory` },
              { "@type": "ListItem", position: 3, name: cityName },
            ],
          })}
        </script>
      </Head>

      {/* Breadcrumb */}
      <div className="border-b border-stone bg-cream/50 px-6 py-3">
        <div className="mx-auto max-w-6xl">
          <nav className="flex items-center gap-2 text-sm text-warm-grey">
            <Link href="/directory" className="transition-colors hover:text-charcoal">
              Directory
            </Link>
            <span>/</span>
            <span className="text-charcoal">{cityName}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Directory</p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            {cityName} estate agents<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Find and compare the top estate agents across {cityName}. Every office, every specialism, all in one place.
          </p>

          {/* Location switcher */}
          {otherLocations.length > 0 && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-warm-grey">
              <span>Also available:</span>
              {otherLocations.map((s) => (
                <Link
                  key={s}
                  href={`/directory/${s}`}
                  className="font-medium text-red transition-colors hover:text-deep-red"
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Filters + grid */}
      <section className="border-t border-stone bg-white px-6 py-12">
        <div className="mx-auto max-w-6xl">
          {/* Filter bar */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by name, address, or postcode\u2026"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-stone bg-cream py-2.5 pl-10 pr-4 text-sm text-charcoal placeholder:text-warm-grey focus:border-red/40 focus:outline-none focus:ring-2 focus:ring-red/10"
              />
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-warm-grey"
              >
                <path
                  fillRule="evenodd"
                  d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* Specialism filter */}
            <select
              value={filterSpecialism}
              onChange={(e) => setFilterSpecialism(e.target.value)}
              className="rounded-lg border border-stone bg-cream px-3 py-2.5 text-sm text-charcoal focus:border-red/40 focus:outline-none focus:ring-2 focus:ring-red/10"
            >
              <option value="">All specialisms</option>
              {specialisms.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            {/* Area filter */}
            <select
              value={filterArea}
              onChange={(e) => setFilterArea(e.target.value)}
              className="rounded-lg border border-stone bg-cream px-3 py-2.5 text-sm text-charcoal focus:border-red/40 focus:outline-none focus:ring-2 focus:ring-red/10"
            >
              <option value="">All areas</option>
              {areas.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>

          {/* Results count */}
          <p className="mt-6 text-sm text-warm-grey">
            Showing {filtered.length} of {offices.length} offices
            {selected.length > 0 && (
              <> · <span className="font-medium text-red">{selected.length} selected for comparison</span></>
            )}
          </p>

          {/* Office grid */}
          <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((office) => (
              <OfficeCard
                key={office.slug}
                office={office}
                location={location}
                selected={selected.includes(office.slug)}
                onToggle={toggleOffice}
              />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-lg font-medium text-charcoal">No offices match your filters</p>
              <p className="mt-2 text-sm text-slate">
                Try broadening your search or clearing the filters.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearch("")
                  setFilterSpecialism("")
                  setFilterArea("")
                }}
                className="mt-4 text-sm font-medium text-red hover:text-deep-red"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* SEO copy */}
      {seoCopy && (
        <section className="border-t border-stone px-6 py-20">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-charcoal">
              {seoCopy.heading}<span className="text-red">.</span>
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-slate">
              {seoCopy.paragraphs.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Get listed CTA */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl border border-stone bg-white p-8 text-center shadow-sm md:p-12">
            <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-red/10">
              <svg viewBox="0 0 24 24" fill="none" stroke="#C41E2A" strokeWidth={1.5} className="size-7">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </div>
            <h2 className="mt-6 font-display text-2xl font-bold text-charcoal md:text-3xl">
              Want to be listed in our directory<span className="text-red">?</span>
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-base text-slate">
              If you&rsquo;re a {cityName}-based estate agent and would like your office featured here,
              we&rsquo;d love to hear from you. Listings are free and include a direct backlink to
              your website.
            </p>
            <div className="mt-8">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red"
              >
                Get in touch
              </Link>
            </div>
            <p className="mt-4 text-sm text-warm-grey">
              Or drop us a line at{" "}
              <a href="mailto:team@quiteright.co.uk" className="font-medium text-red hover:text-deep-red">
                team@quiteright.co.uk
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Are you a {cityName} estate agent<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            Turn your property listings into scroll-stopping video ads. Paste a listing, pick a style,
            and publish across every social platform in under a minute.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Request a demo
            </Link>
            <Link
              href="/how-it-works"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-6 py-3 text-base font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              See how it works
            </Link>
          </div>
        </div>
      </section>

      {/* Compare bar */}
      <CompareBar selected={selected} location={location} onClear={clearSelected} />
    </PublicLayout>
  )
}
